<script lang="ts" setup>
    import { onMounted, reactive, computed } from 'vue';
    import * as ReportModel from '../../lib/Models/Reports/FinancialReportModel';
    import * as _ from 'lodash';
    import TextInput from '../../components/TextInput.vue';
    import { popAlert } from '../../lib/Notification';
    import { IconTypes, LayoutOrientation, DisplayStyle } from '../../Enums/UiEnums';
    import { MessageChannelName } from '../../Enums/ChoiceEnums';
    import ChoiceStateMessageSink from '../../components/ChoiceStateMessageSink.vue';
    import { knowledgeOwlUrl, dataExportRequest } from '../../lib/OfficeMdUrls';
    import Tab from '../../components/Tab.vue';
    import Icon from '../../components/Icon.vue'
    import '../../../css/choice.css';


    const reportData = reactive<{
        reportList?: ReportModel.IReportsLandingMultiData,
        origReportList?: ReportModel.IReportsLandingMultiData,
        origKeywordList?: ReportModel.IReportsLandingKeywordList
        searchBoxPlaceholder: string,
        searchBoxMaxLength: number,
        searchBoxTooltip: string
        searchBoxText: string,
        reportsLandingUrl: string,
        reportClickedUrl?: string,
        showReportList: boolean,
        tabName: string,
        nextTabId: number,
        maxTabs: number,
        selectedTab: string,
        navPaneReportId?: number,

        tabs: Array<{
            id: string,
            label: string,
            url: string
        }>,

    }>({   
        searchBoxPlaceholder: "Search Reports",
        searchBoxMaxLength: 40,
        searchBoxTooltip: "Search for reports by report name or keyword",
        searchBoxText: "",
        reportsLandingUrl: "/officemd/App/reporting/ReportsLanding",
        showReportList: true,
        tabName: "Reports Landing",
        nextTabId: 1,
        maxTabs: 5,
        selectedTab: "ReportList0",
        navPaneReportId: 0,

        tabs: []
    });

    const props = defineProps<{
        reportList: ReportModel.IReportsLandingMultiData
    }>();

    onMounted(async () => {
        try {
            reportData.reportList = props.reportList;
            reportData.origReportList = JSON.parse(JSON.stringify(props.reportList)); 
            reportData.origKeywordList = JSON.parse(JSON.stringify(props.reportList.reportsLandingKeywordData));        
        } catch (error: any) {
            await popAlert(error.message, 'Error Getting Report List');
        }
    });

    const emit = defineEmits<{
        (e: "onChangeTab", selectedTab: any): void,
        (e: "refresh"): void
    }>();

    const groupedReports = computed(() => {
        const groups: any = {};

        if (!reportData.reportList?.reportsLandingReportData) {
            return groups;
        }
        
        for (const report of reportData.reportList?.reportsLandingReportData) {
            if (!groups[report.sectionName]) {
                groups[report.sectionName] = [];
            }
            groups[report.sectionName].push(report);
        }
        return groups;
    });

    const uniqueSectionCount = computed(() => {
        return Object.keys(groupedReports.value).length;
    });

    const filteredTabs = computed(() => {
        return reportData.tabs.filter(tab => "" !== tab.id);
    });

    async function clearSearchText(clearAll: boolean, leaveFullSearchTerm: boolean)
    {
        if (leaveFullSearchTerm) {
            return;
        }

        if (!clearAll && reportData.searchBoxText.length > 1) {
            const newSearchText = reportData.searchBoxText.slice(0, -1);
            await refreshReportListBackToOriginal();
            await searchReports(newSearchText);
        }
        else {
            reportData.searchBoxText = "";
            await refreshReportListBackToOriginal();
        }        
    }

    async function refreshReportListBackToOriginal() {
        reportData.reportList = JSON.parse(JSON.stringify(reportData.origReportList)); 
    }

    async function searchReports(searchTerm: string | undefined) : Promise<void> {    
        //filter report results based on search text - look at reportName, reportDescription, and keywords
        if (!searchTerm || searchTerm?.length === 0) {
            reportData.reportList = props.reportList;
            refreshActiveTab();
            return;
        }

        const searchVal = searchTerm.toLowerCase();

        //let's make sure we have the "full" report list to search against and not the filtered version
        await refreshReportListBackToOriginal();

        if (!reportData.reportList?.reportsLandingReportData) {
            return;
        }

        if (searchVal.length) {
            const reportIdWithKeywordMatch: any =  reportData.reportList.reportsLandingKeywordData?.filter(
                item => item.keyword.toLowerCase().includes(searchVal)
            )
            const reportIdsWithKeywordMatch = reportIdWithKeywordMatch.map((item: any) => item.reportId);

            reportData.reportList.reportsLandingReportData = reportData.reportList.reportsLandingReportData.filter(
                item => reportIdsWithKeywordMatch.includes(item.reportId) ||
                        item.reportName.toLowerCase().includes(searchVal) ||
                        item.reportDescription?.toLowerCase().includes(searchVal)
            );              
        }
    }

    async function refreshPage() {
        emit('refresh');
    }

    function onChangeTab(tab: any, index: any) {
        reportData.selectedTab = tab.id;
        emit("onChangeTab", index);
        displayReportiFrame(tab.id, false);
    }

    async function addReportTab(report: any) {
        const reportUrl = report.reportUrl;
        const reportName = report.reportName;

        if (reportName === "Export Data Request") {
            dataExportRequest(true);
            return;
        }
        //if user clicks a report they already have open in a tab, just switch to that tab, don't open a new one
        const existingTab = reportData.tabs.find(tab => tab.label === report.reportName);
        if (existingTab) {
            reportData.selectedTab = existingTab.id
            displayReportiFrame(existingTab.id, false);
            return;
        }        
        
        /////////////////////////////////////////////////////
        //let's dynamically create the iFrame for the new tab
        const iframe = document.createElement('iframe');        
        const iframeId = `iframeReportsLanding${reportData.nextTabId}`;
        reportData.selectedTab = iframeId;
        await addTab(iframeId, reportName, reportUrl);  

        //calculate the available height (e.g., window height minus some offsets just in case)
        const availableHeight = window.innerHeight - 100; //Adjust 100 as needed for other elements 

        //set iframe attributes        
        iframe.src = reportUrl;
        iframe.id = iframeId; 
        iframe.width = "100%"; 
        iframe.height = `${availableHeight}`;
        iframe.style.border = '1px solid lightgray';
        document.body.appendChild(iframe);
        /////////////////////////////////////////////////////

        //display the desired iFrame and hide the report list
        await displayReportiFrame(iframeId, false);
    }

    async function addTab(iframeId: string, reportLabel: string, reportUrl: string) {        
        //if we have reached the max number of tabs, remove the first tab (index 0) and add the new one
        if (reportData.tabs.length === reportData.maxTabs) {
            reportData.tabs.splice(0, 1);
        }
        reportData.tabs.push({ id: iframeId, label: reportLabel, url: reportUrl });
        reportData.nextTabId++;
    }

    async function displayReportiFrame(iframeId: string, showReportList: boolean) {
        //let's clear out any search terms
        await clearSearchText(false, true);

        if (iframeId === "ReportList0") {
            reportData.selectedTab = "ReportList0";
        }

        reportData.showReportList = showReportList;

        //First identify all report landing iframes
        const iframes = document.querySelectorAll('iframe[id^="iframeReportsLanding"]');

        iframes.forEach(iframe => {
            const iframeEle = iframe as HTMLIFrameElement;
            iframeEle.style.display = (showReportList || iframeEle.id !== iframeId) ? 'none' : 'block';
        });
    }

    const removeTab = (index: number, tab: any) => {
        reportData.tabs.splice(index, 1);
        removeIFrame(tab);
    }

    async function refreshActiveTab() {
        const iframe = document.getElementById(reportData.selectedTab) as HTMLIFrameElement;   

        if (reportData.selectedTab === "ReportList0") {
            //let's clear out any search terms if we are resetting the landing page
            await clearSearchText(true, false);
        }

        //Refresh the selected tab/iframe by setting the src to the same value
        if (iframe) {
            iframe.src = iframe.src;
        }
    }

    async function removeIFrame(tab: any) {
        const iframe = document.getElementById(tab.id) as HTMLIFrameElement;

        //remove the iframe from the DOM then go to Reports Landing if on the tab I'm removing
        if (iframe) {
            iframe.remove();
            const showReportList = tab.id === reportData.selectedTab || reportData.selectedTab === "ReportList0";
            reportData.selectedTab = tab.id === reportData.selectedTab ? "ReportList0" : reportData.selectedTab;
            displayReportiFrame(reportData.selectedTab, showReportList);
        }      
    }

    async function sinkMessageForNewReportId(event: MessageEvent) {
        //Added a broadcast channel to pane_object.js for ReportsLanding that will return the reportId clicked from the left nav pane
        reportData.navPaneReportId = event.data.reportId;

        //let's get the report object from the original reportList based on the reportId
        const fullReportList = JSON.parse(JSON.stringify(reportData.origReportList)); 

        if (fullReportList) {
            const selectedReport = fullReportList.reportsLandingReportData.find((rpt: ReportModel.IReportsLandingReportList) => rpt.reportId === +event.data.reportId);

            if (selectedReport) {
                addReportTab(selectedReport);
            }
            else {
                await popAlert("Report not found", "Error Running Report from Nav Pane");
            }
        }  
    }

</script>

<style scoped>
    .report-container {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-start;  
    }

    /* Each section container takes full width to accommodate internal flexing */
    .report-container > div {
        flex: 1 1 50%; /* 2 columns across for section names */
        padding: 5px;
    }

    .section-header {
        width: 100%; /* Ensure the header takes full width of the section */
    }

    .reports-grid {
        display: flex;
        flex-wrap: wrap;
        list-style-type: none; /* Remove bullet points */
        padding: 0; /* Remove default padding */
        justify-content: flex-start;
    }

    .reports-grid > li {
        flex: 1 1 33%; /* Adjust to use 33% for three columns of reports */
        padding: 5px; /* Optional padding for better spacing */  
    }

    .rowLanding {
        display: flex;
        justify-content: space-between; /* Distribute space between columns */
        margin-bottom: 10px; /* Add spacing below the row */
    }

    .column-left {
        flex: 3;
        text-align: left; 
    }

    .column-right {
        flex: 1;
        text-align: right; 
    }

    .reportlink {
        text-decoration: underline; 
        font-size: .85rem; 
        color: #0D6EFD; 
        cursor: pointer;
    }
</style>

<template>
    <div class="d-flex flex-row pt-2 pb-2 align-items-center">
        <div class="flex-grow-0 pe-1" style="border: 1px solid blue; background-color: #DBEBFF;">
            <span class="ps-1 pointer">
                <Icon 
                    :icon="IconTypes.Clipboard" 
                    :displayStyle="DisplayStyle.Primary" 
                    :class="'text-isalus-primary pointer'"
                    @click="displayReportiFrame('ReportList0', true)"
                    title="Go to Reports Landing"/>
            </span>
            <span class="text-secondary p-1">|</span>
            <span class="pointer">
                <Icon 
                    :icon="IconTypes.Refresh" 
                    :displayStyle="DisplayStyle.Primary" 
                    :class="'text-isalus-primary pointer'"
                    @click="refreshActiveTab"
                    title="Refresh Active Tab"/>
            </span>
            <span class="text-secondary p-1">|</span>
            <span class="pointer">
                <Icon 
                    :icon="IconTypes.Export" 
                    :displayStyle="DisplayStyle.Primary" 
                    :class="'text-isalus-primary pointer'"
                    @click="dataExportRequest(true)"
                    title="Open Export Data Request"/>
            </span>
            <span class="text-secondary p-1">|</span>
            <span class="pointer">
                <Icon 
                    :icon="IconTypes.Information" 
                    :displayStyle="DisplayStyle.Primary" 
                    :class="'text-isalus-primary pointer ps-1 pe-1'"
                    @click="knowledgeOwlUrl(true)"
                    title="Open Knowledge Center Help"/>
            </span>
        </div>
        <div class="flex-grow-0 ps-1 pe-2" v-for="(tab, index) in filteredTabs" :key="tab.id">
            <Tab 
                :value="tab.id" 
                v-model="reportData.selectedTab" 
                :layout="LayoutOrientation.Horz" 
                @onChange="onChangeTab(tab, index)">                    
                {{ tab.label }}
            </Tab> 
            <span>
                <Icon 
                    :icon="IconTypes.Close" 
                    :displayStyle="DisplayStyle.Secondary" 
                    class="text-isalus-primary label-isalus-small pointer"
                    @click="() => removeTab(index, tab)"
                    :title="'Close this report tab'"/>  
            </span>
        </div>
    </div>

    <div v-if="reportData.showReportList">
        <div class="rowLanding pt-1 pb-2">
            <div class="column-left isalus-page-title-large fw-bold">System Reports</div>
            <div class="column-right">
                <TextInput 
                    v-model:text-value="reportData.searchBoxText" 
                    :maxLength="reportData.searchBoxMaxLength"
                    :placeholder="reportData.searchBoxPlaceholder"
                    :title="reportData.searchBoxTooltip"
                    :suffixIcon="IconTypes.Close"
                    suffixTitle="Clear Search"
                    @suffixIconClicked="() => clearSearchText(true, false)"
                    @keydown.backspace="clearSearchText(false, false)"
                    @update:textValue="newVal => searchReports(newVal?.toString())">
                </TextInput>
            </div>
        </div>

        <div class="report-container">
            <div v-if="uniqueSectionCount == 0">
                <span>No reports to display.  Either you do not have access to any reports or your search returned no results.</span>
            </div>
            <div v-else v-for="(reports, sectionName, index) in groupedReports" :key="sectionName" class="border"> 
                <!-- Section Name Header -->
                <span class="section-header label-isalus">{{ sectionName }}</span>
                
                <!-- Reports Grid -->
                <ul class="reports-grid">
                    <li v-for="report in reports" :key="report.reportId" :title="report.reportDescription">
                        <a @click="addReportTab(report)" class="reportlink">{{ report.reportName }}</a>
                    </li>
                    <!-- Add empty list items to ensure 3 columns for reports -->
                    <li v-if="((reports.length % 3) == 2)"></li>
                </ul>
            </div>
            <!-- Add empty div to ensure 2 columns for section names. If total number of sections is odd number, this adds an empty section to stay aligned -->
            <div v-if="uniqueSectionCount % 2 == 1"></div>
        </div>   
    </div> 
    <ChoiceStateMessageSink :channel-name="MessageChannelName.ReportsLanding" @message="sinkMessageForNewReportId"></ChoiceStateMessageSink>
</template>
